import { Link, useParams } from 'react-router-dom'
import { ChevronRight, Lock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { Badge } from '@/components/ui/Badge'
import { ChannelBadge } from '@/features/transactions/components/ChannelBadge'
import { StatusChip } from '@/features/transactions/components/StatusChip'
import { formatDateTime, formatNaira, formatTimeAgo, maskNuban } from '@/lib/format'
import { cn } from '@/lib/cn'
import { useAccountDetail } from './hooks/useAccountDetail'
import { BalanceSnapshot } from './components/BalanceSnapshot'
import { AccountStatusTimeline } from './components/AccountStatusTimeline'
import { AccountRelatedEntities } from './components/AccountRelatedEntities'
import { AccountActions } from './components/AccountActions'
import type { AccountDetail, AccountStatus } from './types'

const TYPE_LABEL: Record<string, string> = {
  savings: 'Savings',
  current: 'Current',
  corporate: 'Corporate',
  domiciliary: 'Domiciliary',
  wallet: 'Wallet',
}

const STATUS_VARIANT: Record<
  AccountStatus,
  React.ComponentProps<typeof Badge>['variant']
> = {
  active: 'success',
  dormant: 'pending',
  closed: 'cancelled',
}

/**
 * AF-132 — Account detail page.
 *
 * Layout (single page, no tabs — analysts decide here in seconds):
 *
 *   ┌────────────── Header (breadcrumbs + identity + actions) ──────────────┐
 *   │                                                                       │
 *   ├──────────────────── 2-col below LG, 1-col on small ───────────────────┤
 *   │  Left col (2/3 width)        │   Right col (1/3 width)                │
 *   │   - Recent transactions      │   - Balance snapshot                   │
 *   │   - Related alerts/cases     │   - Status history                     │
 *   └───────────────────────────────────────────────────────────────────────┘
 */
export function AccountDetailPage() {
  const params = useParams<{ id: string; nuban: string }>()
  const { data, isLoading, isError } = useAccountDetail(params.id, params.nuban)

  if (isLoading) return <AccountDetailSkeleton />
  if (isError || !data) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-muted-fg">
            Couldn't load account {params.nuban}. It may have been removed or
            you don't have permission to view it.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs account={data} />
      <Header account={data} />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          <RecentTransactions account={data} />
          <AccountRelatedEntities
            alerts={data.related_alerts}
            cases={data.related_cases}
          />
        </div>
        <div className="flex flex-col gap-4">
          <AccountActions account={data} />
          <BalanceSnapshot balance={data.balance} />
          <AccountStatusTimeline history={data.status_history} />
        </div>
      </div>
    </div>
  )
}

function Breadcrumbs({ account }: { account: AccountDetail }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-muted-fg">
      <Link to="/customers" className="rounded px-1 py-0.5 hover:bg-muted hover:text-fg">
        Customers
      </Link>
      <ChevronRight size={12} aria-hidden />
      <Link
        to={`/customers/${account.customer.id}`}
        className="rounded px-1 py-0.5 hover:bg-muted hover:text-fg"
      >
        {account.customer.display_name}
      </Link>
      <ChevronRight size={12} aria-hidden />
      <span aria-current="page" className="font-mono text-fg">
        {maskNuban(account.nuban)}
      </span>
    </nav>
  )
}

function Header({ account }: { account: AccountDetail }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-mono text-xl font-semibold tabular-nums">
              {account.nuban}
            </h1>
            <Badge variant={STATUS_VARIANT[account.status]} className="capitalize">
              {account.status}
            </Badge>
            {account.pnd && (
              <Badge variant="failed" className="gap-1">
                <Lock size={10} aria-hidden /> PND
              </Badge>
            )}
            {account.pnd_is_regulatory && (
              <Badge variant="cancelled">Regulatory</Badge>
            )}
          </div>
          <div className="flex flex-col text-sm">
            <span className="font-medium">{account.account_name}</span>
            <span className="text-muted-fg">
              {account.bank.name} ·{' '}
              {TYPE_LABEL[account.account_type] ?? account.account_type} ·
              opened {formatDateTime(account.opened_at)}
              {account.closed_at && ` · closed ${formatDateTime(account.closed_at)}`}
            </span>
            {account.pnd && account.pnd_block_number && (
              <span className="text-xs text-status-failed">
                Active block:{' '}
                <Link to={`/blocks/${account.pnd_block_id}`} className="font-mono underline">
                  {account.pnd_block_number}
                </Link>
                {account.pnd_effective_to &&
                  ` · expires ${formatDateTime(account.pnd_effective_to)}`}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function RecentTransactions({ account }: { account: AccountDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Recent transactions ({account.recent_transactions.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {account.recent_transactions.length === 0 ? (
          <p className="text-sm text-muted-fg">
            No transactions on this account in the last 30 days.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {account.recent_transactions.map((tx) => (
              <li key={tx.id}>
                <Link
                  to={`/transactions/${tx.id}`}
                  className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto_auto_1rem] items-center gap-3 rounded border border-transparent px-2 py-2 text-sm transition-colors hover:border-border hover:bg-muted"
                >
                  <ChannelBadge channel={tx.channel} />
                  <span className="flex flex-col truncate">
                    <span className="font-medium truncate">
                      {tx.counterparty_label ?? 'Unknown counterparty'}
                    </span>
                    <span className="font-mono text-xs text-muted-fg">
                      {tx.transaction_ref}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'font-mono text-sm tabular-nums',
                      tx.outbound ? 'text-status-failed' : 'text-status-success',
                    )}
                  >
                    {tx.outbound ? '−' : '+'}
                    {formatNaira(tx.amount_minor)}
                  </span>
                  <StatusChip status={tx.status} />
                  <span className="text-xs text-muted-fg" title={formatDateTime(tx.occurred_at)}>
                    {formatTimeAgo(tx.occurred_at)}
                  </span>
                  <ChevronRight size={12} className="text-muted-fg" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function AccountDetailSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-5 w-72" />
      <Skeleton className="h-24 w-full" />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Skeleton className="h-72 w-full" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </div>
  )
}
